import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Info, Award, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const noteZoneColor = (note) => {
  if (note <= 2) return "#22c55e";
  if (note <= 4) return "#f18e00";
  return "#ef4444";
};

const InfoSection = ({ id, icon: Icon, title, active, onToggle, children }) => (
  <div className="border border-white/10 bg-black/40">
    <button
      data-testid={`wizard-info-${id}-toggle`}
      onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
    >
      <span className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-brand-orange" />
        <span className="text-sm font-semibold text-white">{title}</span>
      </span>
      <ChevronDown
        className={`h-4 w-4 text-white/40 transition-transform duration-300 ${active ? "rotate-180" : ""}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {active && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <div className="px-5 pb-5 pt-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const InfoPanel = ({ hf }) => {
  const [open, setOpen] = useState(null); // 'warum' | 'best' | null
  const toggle = (k) => setOpen((o) => (o === k ? null : k));

  return (
    <div className="mt-6 grid sm:grid-cols-2 gap-3">
      <InfoSection id="warum" icon={Info} title="Warum dieses Feld über Wert entscheidet" active={open === "warum"} onToggle={toggle}>
        <p className="text-sm text-white/65 leading-relaxed">{hf.warum}</p>
      </InfoSection>
      <InfoSection id="best" icon={Award} title="Best Practice: So sieht Spitzensteuerung aus" active={open === "best"} onToggle={toggle}>
        <ul className="space-y-2">
          {hf.best_practices.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
              <span className="text-sm text-white/65 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
        {hf.beispiel && (
          <div className="mt-4 border-l-2 border-brand-orange/60 pl-3">
            <span className="text-[0.65rem] uppercase tracking-[0.15em] text-brand-orange">Aus der Praxis</span>
            <p className="mt-1 text-sm text-white/70 italic leading-relaxed">{hf.beispiel}</p>
          </div>
        )}
      </InfoSection>
    </div>
  );
};

export const Wizard = ({ fields, branchen, branche, setBranche, answers, setAnswer, onComplete, onBackToStart }) => {
  const [step, setStep] = useState(0);
  const hf = fields[step];
  const total = fields.length;
  const selected = answers[hf.id];
  const isLast = step === total - 1;
  const brancheOptions = branchen || {
    allgemein: "Allgemein (branchenunabhängig)",
    dienstleistung: "Dienstleistung & Beratung",
    produktion: "Produktion & Maschinenbau",
  };

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const handleBack = () => {
    if (step === 0) {
      onBackToStart();
    } else {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="wizard" className="max-w-6xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
      {/* Progress */}
      <div className="mb-10">
        {step === 0 && setBranche && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <label htmlFor="branche-select" className="text-xs tracking-[0.15em] uppercase text-white/50">
              Ihre Branche
            </label>
            <select
              id="branche-select"
              data-testid="wizard-branche-select"
              value={branche}
              onChange={(e) => setBranche(e.target.value)}
              className="h-10 bg-[#111] border border-white/15 text-white text-sm px-3 focus:outline-none focus:border-brand-orange sm:min-w-[280px]"
            >
              {Object.entries(brancheOptions).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <span className="text-xs text-white/35">schärft Ihre KI-Benchmarks</span>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
            Handlungsfeld {step + 1} / {total}
          </span>
          <span className="text-xs text-white/40">
            {Object.keys(answers).length} von {total} bewertet
          </span>
        </div>
        <div className="flex gap-1.5" data-testid="wizard-progress">
          {fields.map((f, i) => (
            <div
              key={f.id}
              className="h-1.5 flex-1 transition-colors duration-300"
              style={{
                backgroundColor:
                  answers[f.id] != null
                    ? noteZoneColor(answers[f.id])
                    : i === step
                    ? "#f18e00"
                    : "#2a2a2a",
              }}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={hf.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35 }}
        >
          <div className="flex items-start gap-4 mb-2">
            <span className="font-display text-brand-orange text-4xl sm:text-5xl leading-none">
              {String(hf.id).padStart(2, "0")}
            </span>
            <div>
              <h2 className="font-display uppercase text-2xl sm:text-3xl tracking-tight text-white">
                {hf.name}
              </h2>
              <p className="mt-1 text-brand-orange/90 italic text-sm">"{hf.tagline}"</p>
            </div>
          </div>
          <p className="text-sm text-white/60 leading-relaxed max-w-3xl mt-4 border-l-2 border-white/10 pl-4">
            {hf.inhalt}
          </p>

          {/* Warum + Best Practice (vor der Benotung) */}
          <InfoPanel hf={hf} />

          <p className="mt-10 mb-4 text-sm font-semibold text-white">
            Welche Beschreibung trifft am ehesten auf Ihr Unternehmen zu?
          </p>

          {/* Grade cards, Note 6 (Ausgangslage) -> 1 (Spitze) */}
          <div className="grid md:grid-cols-2 gap-3">
            {[6, 5, 4, 3, 2, 1].map((note) => {
              const active = selected === note;
              const color = noteZoneColor(note);
              return (
                <button
                  key={note}
                  data-testid={`wizard-card-hf${hf.id}-note${note}`}
                  onClick={() => setAnswer(hf.id, note)}
                  className={`group text-left p-5 border transition-colors duration-200 relative ${
                    active
                      ? "bg-brand-orange/5 border-brand-orange orange-glow"
                      : "bg-card border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span
                        className="h-9 w-9 flex items-center justify-center font-display text-sm shrink-0"
                        style={{ backgroundColor: color, color: "#000" }}
                      >
                        {note}
                      </span>
                      <span className="text-[0.7rem] tracking-[0.15em] uppercase text-white/40">
                        Note {note}
                      </span>
                    </div>
                    <span
                      className={`h-6 w-6 rounded-full border flex items-center justify-center transition-colors ${
                        active ? "bg-brand-orange border-brand-orange" : "border-white/20"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5 text-black" />}
                    </span>
                  </div>
                  <p className="text-sm text-white/75 leading-relaxed">{hf.grades[note]}</p>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Footer nav */}
      <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
        <Button
          data-testid="wizard-back-btn"
          onClick={handleBack}
          variant="ghost"
          className="h-12 px-5 rounded-none text-white/60 hover:text-white hover:bg-white/5"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {step === 0 ? "Zur Startseite" : "Zurück"}
        </Button>
        <Button
          data-testid="wizard-next-btn"
          onClick={handleNext}
          disabled={selected == null}
          className="group h-12 px-7 rounded-none bg-brand-orange text-black hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed font-bold uppercase tracking-wider text-sm transition-colors duration-300"
        >
          {isLast ? (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Lagebericht erstellen
            </>
          ) : (
            <>
              Weiter
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Wizard;
